import { Badge, TextTooltip } from '@components'
import { getDashboardPageUrl, getProductPageUrl, getWorkflowsPageUrl } from '@constants'
import { BusinessMenu } from '@containers/BusinessMenu/BusinessMenu'
import { Scrollbar } from 'react-scrollbars-custom'
import {
  FeatureWrapper,
  FlagWrapper,
  PermissionBoundary,
  PermissionSet,
  isNYBusiness,
  isVATObliged,
  useBusinessContext,
  usePermission,
  usePermissionsLoaded
} from '@context'
import { useLocalStorage, useTheme } from '@hooks'
import { BusinessForms, isBusinessBillingLocked } from '@query'
import { useScreen } from '@utils'
import { motion, useInView } from 'framer-motion'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FaAddressCard,
  FaBook,
  FaCalculator,
  FaCalendarAlt,
  FaChartLine,
  FaChevronCircleRight,
  FaCloud,
  FaCog,
  FaFileDownload,
  FaInfoCircle,
  FaMask,
  FaProjectDiagram,
  FaShoppingBasket,
  FaStream,
  FaThLarge
} from 'react-icons/fa'
import { matchPath } from 'react-router'
import { NavLink, useLocation } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { DateRangeSelect } from './components/DateRangeSelect'
import { MenuItemShimmer } from './components/MenuItemShimmer'
import cn from 'classnames'
import { FaEllipsis, FaWallet } from 'react-icons/fa6'
import { PlanBadge } from './PlanBadge'
import { useSettingsMenuNotification } from './useSettingsMenuNotification'
import { TrialTracker } from './TrialTracker'

interface MenuItemNotification {
  icon: React.ReactNode
  message: string
}

interface MenuItemProps {
  icon?: React.ReactElement
  name: React.ReactNode
  path?: string
  target?: React.HTMLAttributeAnchorTarget
  isNarrow: boolean
  isNew?: boolean
  notification?: MenuItemNotification
  disabled?: boolean
}

const menuItemContentTransition = { duration: 0.18, ease: 'easeInOut' as const }

const MenuItem: React.FC<MenuItemProps> = ({
  icon,
  name,
  target,
  path,
  isNew,
  isNarrow,
  notification,
  disabled = false
}) => {
  if (!path && !disabled) throw new Error('Path is required if the menu item is not disabled')

  const { pathname } = useLocation()
  const normalizedPath = path?.split('?')[0]

  const isActive =
    !disabled &&
    !!normalizedPath &&
    matchPath(pathname, {
      path: normalizedPath,
      exact: true,
      strict: false
    })

  return (
    <StyledMenuItem
      to={disabled ? '#' : path || '#'}
      target={target}
      $disabled={disabled}
      aria-disabled={disabled}
    >
      <MenuItemContent>
        {isActive && <SelectionBackground layoutId="selected-background" />}
        <MenuItemContentWide
          initial={false}
          animate={{ opacity: isNarrow ? 0 : 1 }}
          transition={menuItemContentTransition}
          $isOverlay={true}
        >
          {icon && <MenuItemIconWrapper>{icon}</MenuItemIconWrapper>}
          <MenuItemLabel>{name}</MenuItemLabel>

          {isNew && (
            <BadgeWrapper>
              <Badge type="success">{'New'}</Badge>
            </BadgeWrapper>
          )}

          {notification && (
            <TextTooltip tooltip={notification.message} placement="bottom">
              <AnimatedIconWrapper>{notification.icon}</AnimatedIconWrapper>
            </TextTooltip>
          )}
        </MenuItemContentWide>

        <MenuItemContentNarrow
          initial={false}
          animate={{ opacity: isNarrow ? 1 : 0 }}
          transition={menuItemContentTransition}
          $isOverlay={true}
        >
          {icon && <MenuItemIconWrapper>{icon}</MenuItemIconWrapper>}
        </MenuItemContentNarrow>
      </MenuItemContent>
    </StyledMenuItem>
  )
}

const MenuSectionHeader: React.FC<{ isNarrow: boolean; title: string }> = ({ isNarrow, title }) => (
  <MenuGroupHeader initial={false} animate={isNarrow ? 'hide' : 'show'}>
    {title}
  </MenuGroupHeader>
)

interface MainMenuProps {
  width?: number | string
  narrowWidth?: number | string
  isTogglable?: boolean
}

export const MainMenu: React.FC<MainMenuProps> = ({
  width = 280,
  narrowWidth = 80,
  isTogglable = false
}) => {
  const { width: sWidth, breakPoints } = useScreen()
  const { slug, data: business } = useBusinessContext()
  const is_ny = isNYBusiness(business)
  const vat_obliged = isVATObliged(business)
  const isDemoBusiness = business?.form === BusinessForms.DEMO
  const isBillingLocked = isBusinessBillingLocked(business)
  const permissionsLoaded = usePermissionsLoaded()
  const canViewDateRanges = usePermission(PermissionSet.DATE_RANGE_VIEW)

  const [t] = useTranslation()
  const [_isNarrow, setNarrow] = useLocalStorage<boolean>('menuWidth', sWidth < breakPoints.lg)
  const isNarrow = isTogglable ? _isNarrow : false
  const theme = useTheme()
  const menuOnTopIndicatorRef = useRef<HTMLDivElement>(null)
  const menuOnBottomIndicatorRef = useRef<HTMLDivElement>(null)
  const showTopIndicator = !useInView(menuOnTopIndicatorRef)
  const showBottomIndicator = !useInView(menuOnBottomIndicatorRef)

  const { notification: settingsNotification, settingsPath } = useSettingsMenuNotification()

  const itemShimmer = (width: number) => <MenuItemShimmer width={width} isNarrow={isNarrow} />

  const sectionShimmer = (titleKey: string, widths: number[]) => (
    <StyledMenuGroup>
      <MenuSectionHeader isNarrow={isNarrow} title={t(titleKey)} />
      {widths.map((width, index) => (
        <MenuItemShimmer key={`${titleKey}-${index}`} width={width} isNarrow={isNarrow} />
      ))}
    </StyledMenuGroup>
  )
  const frontpageMenuItem = (
    <MenuItem
      icon={<FaChartLine />}
      isNarrow={isNarrow}
      name={t('mainMenu.frontpage')}
      path={getDashboardPageUrl(slug)}
    />
  )

  const salariesMenuItem = (disabled = false) => (
    <MenuItem
      icon={<FaWallet />}
      name={t('mainMenu.salaries.title')}
      path={`/${slug}/salaries`}
      target="_blank"
      isNarrow={isNarrow}
      disabled={disabled}
    />
  )

  const salariesFallbackContent = isDemoBusiness ? (
    <TextTooltip tooltip={t('mainMenu.salaries.disabledTooltip')} style={{ width: '100%' }}>
      <DisabledTooltipWrapper>{salariesMenuItem(true)}</DisabledTooltipWrapper>
    </TextTooltip>
  ) : null

  return (
    <StyledMainMenu
      initial={false}
      animate={{ width: isNarrow ? narrowWidth : width }}
      transition={{ type: 'tween', duration: 0.24, ease: [0.4, 0, 0.2, 1] }}
    >
      <BusinessMenu>
        <BusinessMenuWrapper $isNarrow={isNarrow} aria-label={t('businessesPage.yourBusinesses')}>
          {isNarrow && <FaThLarge />}
          <BusinessName
            $isNarrow={isNarrow}
            initial={false}
            animate={{
              opacity: isNarrow ? 0 : 1,
              scaleX: isNarrow ? 0.92 : 1
            }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            {business?.name}
          </BusinessName>
          {!isNarrow && (
            <BusinessMenuChevron aria-hidden={true}>
              <FaEllipsis />
            </BusinessMenuChevron>
          )}
        </BusinessMenuWrapper>
      </BusinessMenu>

      {!isBillingLocked && (
        <>
          {(!permissionsLoaded || canViewDateRanges) && (
            <DateRangeSelectWrapper>
              <DateRangeSelect compact={isNarrow} />
            </DateRangeSelectWrapper>
          )}

          <MenuWrapper
            $isNarrow={isNarrow}
            initial={false}
            className={cn({
              showTopIndicator,
              showBottomIndicator
            })}
          >
            <Scrollbar noScrollX={true}>
              <StyledMenuGroup>
                <div ref={menuOnTopIndicatorRef} />
                <PermissionBoundary
                  required={PermissionSet.REPORTING_VIEW}
                  placeholder={itemShimmer(48)}
                >
                  {frontpageMenuItem}
                </PermissionBoundary>
                <PermissionBoundary
                  required={PermissionSet.BOOKKEEPING_VIEW}
                  placeholder={itemShimmer(52)}
                >
                  <MenuItem
                    icon={<FaStream />}
                    isNarrow={isNarrow}
                    name={t('mainMenu.documents')}
                    path={`/${slug}/documents`}
                  />
                </PermissionBoundary>
                <PermissionBoundary
                  required={PermissionSet.ACCOUNTS_VIEW}
                  placeholder={itemShimmer(45)}
                >
                  <MenuItem
                    icon={<FaBook />}
                    isNarrow={isNarrow}
                    name={t('mainMenu.accounts')}
                    path={`/${slug}/accounts`}
                  />
                </PermissionBoundary>
                <PermissionBoundary
                  required={PermissionSet.FILES_VIEW}
                  placeholder={itemShimmer(55)}
                >
                  <MenuItem
                    icon={<FaCloud />}
                    isNarrow={isNarrow}
                    name={t('mainMenu.files')}
                    path={`/${slug}/files`}
                  />
                </PermissionBoundary>
              </StyledMenuGroup>

              <FeatureWrapper feature="invoicing">
                <PermissionBoundary
                  required={[...PermissionSet.INVOICING_VIEW, ...PermissionSet.CONTACT_VIEW]}
                  placeholder={sectionShimmer('mainMenu.invoicingTitle', [50, 42, 48])}
                >
                  <StyledMenuGroup>
                    <MenuSectionHeader isNarrow={isNarrow} title={t('mainMenu.invoicingTitle')} />
                    <PermissionBoundary required={PermissionSet.INVOICING_VIEW}>
                      <MenuItem
                        icon={<FaCalculator />}
                        isNarrow={isNarrow}
                        name={t('mainMenu.invoices')}
                        path={`/${slug}/invoicing`}
                      />
                    </PermissionBoundary>
                    <PermissionBoundary required={PermissionSet.CONTACT_VIEW}>
                      <MenuItem
                        icon={<FaAddressCard />}
                        isNarrow={isNarrow}
                        name={t('mainMenu.contacts')}
                        path={`/${slug}/contacts`}
                      />
                    </PermissionBoundary>
                    <PermissionBoundary required={PermissionSet.INVOICING_VIEW}>
                      <MenuItem
                        icon={<FaShoppingBasket />}
                        isNarrow={isNarrow}
                        name={t('mainMenu.products')}
                        path={getProductPageUrl(slug)}
                      />
                    </PermissionBoundary>
                  </StyledMenuGroup>
                </PermissionBoundary>
              </FeatureWrapper>

              <PermissionBoundary
                required={PermissionSet.REPORTING_VIEW}
                placeholder={sectionShimmer('mainMenu.reportingTitle', [52, 45, 55])}
              >
                <StyledMenuGroup>
                  <MenuSectionHeader isNarrow={isNarrow} title={t('mainMenu.reportingTitle')} />
                  {!is_ny && (
                    <MenuItem
                      icon={<FaCalendarAlt />}
                      isNarrow={isNarrow}
                      name={t('mainMenu.periods')}
                      path={`/${slug}/accounting`}
                    />
                  )}
                  {vat_obliged && (
                    <MenuItem
                      icon={<FaMask />}
                      isNarrow={isNarrow}
                      name={t('mainMenu.vatPeriods')}
                      path={`/${slug}/vat`}
                    />
                  )}
                  <MenuItem
                    icon={<FaFileDownload />}
                    isNarrow={isNarrow}
                    name={t('mainMenu.reports')}
                    path={`/${slug}/reporting`}
                  />
                </StyledMenuGroup>
              </PermissionBoundary>

              <StyledMenuGroup>
                <MenuSectionHeader isNarrow={isNarrow} title={t('mainMenu.generalTitle')} />
                <FeatureWrapper feature="salaries" alternativeContent={salariesFallbackContent}>
                  <PermissionBoundary required={PermissionSet.SALARY} placeholder={itemShimmer(50)}>
                    {salariesMenuItem()}
                  </PermissionBoundary>
                </FeatureWrapper>
                <FlagWrapper flag="workflows">
                  <PermissionBoundary
                    required={PermissionSet.WORKFLOWS_HUB}
                    placeholder={itemShimmer(50)}
                  >
                    <MenuItem
                      icon={<FaProjectDiagram />}
                      isNarrow={isNarrow}
                      name={t('mainMenu.workflows')}
                      path={getWorkflowsPageUrl(slug)}
                    />
                  </PermissionBoundary>
                </FlagWrapper>
                <MenuItem
                  icon={<FaCog />}
                  isNarrow={isNarrow}
                  name={t('mainMenu.settings')}
                  path={settingsPath}
                  notification={settingsNotification}
                />
                <div ref={menuOnBottomIndicatorRef} />
              </StyledMenuGroup>
            </Scrollbar>
          </MenuWrapper>
        </>
      )}

      {isBillingLocked && <LockedMenuSpacer />}

      <MenuFooter>
        <FooterRow>
          {!isNarrow && (
            <FooterLink href={theme.brandUrl} target="_blank" rel="noreferrer">
              <AnimatedLogoWrapper key="full-logo">
                <FooterImg $image={theme.assets.logoLight} />
              </AnimatedLogoWrapper>
            </FooterLink>
          )}

          {isTogglable && (
            <MenuToggle
              className="menu-toggle"
              onClick={() => setNarrow(!isNarrow)}
              animate={{ rotate: isNarrow ? 0 : 180 }}
              transition={{ type: 'tween', duration: 0.24, ease: [0.4, 0, 0.2, 1] }}
            >
              <FaChevronCircleRight />
            </MenuToggle>
          )}
        </FooterRow>

        <PlanBadge
          slug={slug}
          business={business}
          isNarrow={isNarrow}
          onExpand={() => setNarrow(false)}
        />
        <TrialTracker slug={slug} business={business} isNarrow={isNarrow} />

        {isDemoBusiness && !isBillingLocked && (
          <TextTooltip
            tooltip={t('settings.demo.trialDescription')}
            placement="top-start"
            openDelay={400}
            closeDelay={0}
            style={{ width: '100%' }}
          >
            <StyledTrialingBadge>
              <FaInfoCircle />
              {!isNarrow && (
                <span>{t('settings.demo.trialTitle', { daysLeft: business?.demo_days_left })}</span>
              )}
            </StyledTrialingBadge>
          </TextTooltip>
        )}
      </MenuFooter>
    </StyledMainMenu>
  )
}

const StyledMainMenu = styled(motion.div)`
  height: 100%;
  background: ${({ theme }) => theme.colors.menuBg};
  display: flex;
  flex-direction: column;
  position: relative;
  white-space: nowrap;
  overflow: hidden;

  // hide scrollbar
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  .menu-toggle {
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover {
    .menu-toggle {
      opacity: 1;
    }
  }
`

const MenuToggle = styled(motion.div)`
  cursor: pointer;
  width: 1.2rem;
  height: 1.2rem;
  border-radius: 1000px;
  z-index: 1;
  display: flex;
  justify-content: center;
  padding: 1rem;

  svg {
    align-self: center;
    transition: fill 0.4s;
    fill: ${({ theme }) => theme.colors.neutralGray};
    width: 1.3rem;
    min-width: 1.3rem;
    height: 1.3rem;
    min-height: 1.3rem;
    opacity: 0.6;
  }
`

const menuItemMargin = css`
  margin: ${({ theme }) => theme.spacing.xxs}rem;
`

const menuItemInnerPadding = css`
  padding: ${({ theme }) => `${theme.spacing.sm}rem ${theme.spacing.md}rem`};

  @media (max-width: 1024px) {
    padding: ${({ theme }) => `${theme.spacing.xs}rem ${theme.spacing.sm}rem`};
  }
`

const MenuFooter = styled(motion.div).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 }
})`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  opacity: 0.8;
  margin: ${({ theme }) => theme.spacing.md}rem;
`

const FooterRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  ${menuItemMargin}
  ${menuItemInnerPadding}
`

const AnimatedLogoWrapper = styled(motion.div).attrs({
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 }
})`
  width: 100%;
`

const FooterImg = styled.div<{ $image: string }>`
  background-image: url(${({ $image }) => $image});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: left center;
  align-self: flex-end;
  max-width: ${({ theme }) => theme.assets.menuLogo?.maxWidth ?? '140px'};
  height: ${({ theme }) => theme.assets.menuLogo?.height ?? '22px'};
`
const LockedMenuSpacer = styled.div`
  flex: 1;
`

const MenuWrapper = styled(motion.div)<{ $isNarrow: boolean }>`
  flex: 1;
  position: relative;
  overflow: hidden;
  padding: ${({ $isNarrow, theme }) =>
    $isNarrow ? `0 0 0 ${theme.spacing.md}rem` : `0 ${theme.spacing.md}rem`};

  &.showTopIndicator {
    border-top: 1px solid ${({ theme }) => theme.colors.metalGray}66;
  }

  &.showBottomIndicator {
    border-bottom: 1px solid ${({ theme }) => theme.colors.metalGray}66;
  }

  .ScrollbarsCustom-Wrapper {
    ${({ $isNarrow }) =>
      $isNarrow ? 'inset: 0 15px 0 0 !important;' : 'inset: 0 20px 0 0 !important;'}
  }

  .ScrollbarsCustom-Track {
    top: 0 !important;
    ${({ $isNarrow }) =>
      $isNarrow
        ? `
      width: 6px !important;
      margin-right: 4px !important;
     `
        : 'width: 10px !important;'}
    background: #ffffff0e !important;
  }

  .ScrollbarsCustom-ThumbY {
    background: #ffffff0e !important;
  }
`

const BusinessMenuWrapper = styled.button.attrs({ type: 'button' })<{ $isNarrow: boolean }>`
  margin: ${({ theme }) => theme.spacing.md}rem;
  height: 2.4rem;
  padding: ${({ $isNarrow, theme }) =>
    $isNarrow
      ? `${theme.spacing.sm}rem ${theme.spacing.sm}rem`
      : `${theme.spacing.sm}rem ${theme.spacing.md}rem`};
  color: ${({ theme }) => theme.colors.neutralGray};
  display: flex;
  align-items: center;
  justify-content: ${({ $isNarrow }) => ($isNarrow ? 'center' : 'flex-start')};
  font-weight: 500;
  cursor: pointer;
  overflow: hidden;
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: transparent;
  text-align: left;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover,
  &:focus,
  &:focus-visible {
    background: #ffffff12;
    color: white;
    outline: none;
  }

  &[aria-expanded='true'] {
    background: #ffffff1a;
    color: white;
  }

  svg {
    color: currentColor;
    width: ${({ theme }) => theme.fontSize.md}rem;
    height: ${({ theme }) => theme.fontSize.md}rem;
    min-width: ${({ theme }) => theme.fontSize.md}rem;
    min-height: ${({ theme }) => theme.fontSize.md}rem;
  }
`

const BusinessName = styled(motion.span)<{ $isNarrow: boolean }>`
  flex: ${({ $isNarrow }) => ($isNarrow ? '0 0 0' : '1 1 auto')};
  width: ${({ $isNarrow }) => ($isNarrow ? '0' : 'auto')};
  overflow: hidden;
  text-overflow: ellipsis;
  transform-origin: left center;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const BusinessMenuChevron = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: ${({ theme }) => theme.spacing.xs}rem;
`

const StyledMenuGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
  width: 100%;
`

const BadgeWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

const SelectionBackground = styled(motion.div).attrs({
  transition: { type: 'spring', duration: 0.6 }
})`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  height: 100%;
  width: 100%;
  background: #00000040;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
`

const MenuGroupHeader = styled(motion.h4).attrs({
  variants: {
    hide: { x: 0, opacity: 0 },
    show: { x: 0, opacity: 1 }
  }
})`
  height: 1rem;
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 1px;
  margin-top: ${({ theme }) => theme.spacing.md}rem;
  color: ${({ theme }) => theme.colors.neutralWhite}33;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  padding: 0 ${({ theme }) => theme.spacing.md}rem;

  @media (max-width: 1024px) {
    padding: 0 ${({ theme }) => theme.spacing.sm}rem;
  }
`

const MenuItemContent = styled.div`
  position: relative;
  isolation: isolate;
  width: 100%;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  line-height: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.neutralGray};
  min-height: 2.6rem;
  @media (max-width: 1024px) {
    min-height: 2.2rem;
  }
  overflow: visible;

  ${menuItemMargin}

  &:hover {
    opacity: 0.8;
  }
`

const MenuItemContentWide = styled(motion.div)<{ $isOverlay?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  ${menuItemInnerPadding}
  position: ${({ $isOverlay }) => ($isOverlay ? 'absolute' : 'relative')};
  inset: ${({ $isOverlay }) => ($isOverlay ? '0' : 'auto')};
  z-index: 1;
`

const MenuItemContentNarrow = styled(motion.div)<{ $isOverlay?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  ${menuItemInnerPadding}
  position: ${({ $isOverlay }) => ($isOverlay ? 'absolute' : 'relative')};
  inset: ${({ $isOverlay }) => ($isOverlay ? '0' : 'auto')};
  z-index: 1;
`

const MenuItemIconWrapper = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 ${({ theme }) => theme.fontSize.md}rem;
  width: ${({ theme }) => theme.fontSize.md}rem;
  min-width: ${({ theme }) => theme.fontSize.md}rem;

  & > svg {
    display: block;
    color: ${({ theme }) => theme.colors.neutralGray};
    width: ${({ theme }) => theme.fontSize.md}rem;
    height: ${({ theme }) => theme.fontSize.md}rem;
    min-width: ${({ theme }) => theme.fontSize.md}rem;
    min-height: ${({ theme }) => theme.fontSize.md}rem;
  }
`

const MenuItemLabel = styled.div`
  min-width: 0;
  flex: 1 1 auto;
  white-space: nowrap;
  overflow: hidden;
`

const StyledMenuItem = styled(NavLink)<{ $disabled: boolean }>`
  position: relative;
  display: flex;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  color: white;
  width: 100%;
  align-items: center;
  justify-content: center;
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }

  & > svg {
    height: 2.6rem;
  }
`

const StyledTrialingBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  background: ${({ theme }) => theme.colors.dashboardNeg}22;
  border: 2px solid ${({ theme }) => theme.colors.dashboardNeg}66;
  color: ${({ theme }) => theme.colors.dashboardNeg} !important;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  font-weight: bold;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  overflow: hidden;
  width: 100%;
  justify-content: center;
  text-align: center;

  svg {
    min-width: 0.8rem;
    width: 0.8rem;
    min-height: 0.8rem;
    height: 0.8rem;
  }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const AnimatedIconWrapper = styled(motion.div).attrs({
  initial: { scale: 1 },
  animate: {
    scale: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.2, 1]
  },
  transition: {
    repeat: Infinity,
    duration: 4
  }
})``

const DateRangeSelectWrapper = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.lg}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
`

const DisabledTooltipWrapper = styled.span`
  cursor: not-allowed;
  display: flex;
  width: 100%;
`

const FooterLink = styled.a`
  text-decoration: none;
  flex: 1;
`
